import React from 'react';
import { useTranslation } from 'react-i18next';
import { MainMatchesData } from './MainMatchesData';
import {
  FaHome,
  FaPlane,
  FaClock,
  FaShieldAlt,
  FaHistory,
  FaExchangeAlt,
} from 'react-icons/fa';
import { IoStatsChart } from 'react-icons/io5';
import { BiFootball } from 'react-icons/bi';

interface Props {
  selectedItemData: MainMatchesData;
  columnToShow?: 'left' | 'center' | 'right' | 'all';
}

export const TeamsAverageGoals = ({
  selectedItemData,
  columnToShow = 'all',
}: Props) => {
  const { t } = useTranslation();

  if (!selectedItemData) {
    return <div>{t('common.noDataAvailable')}</div>;
  }

  const showLeft = columnToShow === 'left' || columnToShow === 'all';
  const showCenter = columnToShow === 'center' || columnToShow === 'all';
  const showRight = columnToShow === 'right' || columnToShow === 'all';

  const tableStyle =
    columnToShow === 'all'
      ? { width: '500px', fontSize: '12px' }
      : { marginRight: '20px' };

  const getHeaderStyle = (isHomeTeam: boolean) => {
    return isHomeTeam
      ? { background: 'linear-gradient(to right, #065f46, #064e3b)' }
      : { background: 'linear-gradient(to right, #1e40af, #1e3a8a)' };
  };

  const getValueClass = (scored: number, conceded: number): string => {
    const diff = scored - conceded;
    if (diff > 1) return 'text-green-500 font-bold';
    if (diff > 0) return 'text-green-400 font-bold';
    if (diff === 0) return 'text-gray-300 font-bold';
    if (diff > -1) return 'text-yellow-400 font-bold';
    return 'text-red-400 font-bold';
  };

  const HeaderWithIcon = ({
    icon: Icon,
    text,
    style = {},
  }: {
    icon: React.ElementType;
    text: string;
    style?: React.CSSProperties;
  }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      <Icon style={{ marginRight: '0.5rem' }} /> {text}
    </div>
  );

  const ColumnHeaderWithIcon = ({
    icon: Icon,
    text,
  }: {
    icon: React.ElementType;
    text: string;
  }) => (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon style={{ marginRight: '0.25rem' }} /> {text}
    </span>
  );

  const formatNotAvailableValue = (value: number | null | undefined) => {
    return value === 0 || value === null || value === undefined ? 'N/A' : value;
  };

  return (
    <div className="TeamsAverageGoals">
      <div className="last5container" style={{ display: 'flex' }}>
        {/* LEFT COLUMN */}
        {showLeft && (
          <div className="left-table">
            <table style={{ fontSize: '12px' }}>
              <thead>
                <tr>
                  <th colSpan={4} style={getHeaderStyle(true)}>
                    <HeaderWithIcon
                      icon={FaClock}
                      text={t('teamsAverageGoals.averageGoalsHalfTime')}
                    />
                  </th>
                </tr>
                <tr>
                  <th
                    colSpan={3}
                    style={{ backgroundColor: 'rgba(45, 212, 191, 0.1)' }}
                  >
                    <HeaderWithIcon
                      icon={FaHome}
                      text={t('teamsAverageGoals.homeTeam')}
                    />
                  </th>
                </tr>
                <tr>
                  <th>
                    <ColumnHeaderWithIcon
                      icon={BiFootball}
                      text={t('teamsAverageGoals.scored')}
                    />
                  </th>
                  <th></th>
                  <th>
                    <ColumnHeaderWithIcon
                      icon={FaShieldAlt}
                      text={t('teamsAverageGoals.conceded')}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-center text-teal-400 font-bold">
                    {formatNotAvailableValue(
                      selectedItemData.AverageGoals.HT_HomeScoredAtHome,
                    )}
                  </td>
                  <td className="text-center text-xs text-gray-400">
                    <span className="flex items-center justify-center">
                      <FaHome className="mr-1" />{' '}
                      {t('teamsAverageGoals.onlyHome')}
                    </span>
                  </td>
                  <td className="text-center text-red-400 font-bold">
                    {formatNotAvailableValue(
                      selectedItemData.AverageGoals.HT_HomeConcededAtHome,
                    )}
                  </td>
                </tr>

                <tr>
                  <td className="text-center text-teal-400 font-bold">
                    {formatNotAvailableValue(
                      selectedItemData.AverageGoals.HT_HomeScoredLast5AtHome,
                    )}
                  </td>
                  <td className="text-center text-xs text-gray-400">
                    <span className="flex items-center justify-center">
                      <FaHistory className="mr-1" />{' '}
                      {t('teamsAverageGoals.last5Home')}
                    </span>
                  </td>
                  <td className="text-center text-red-400 font-bold">
                    {formatNotAvailableValue(
                      selectedItemData.AverageGoals.HT_HomeConcededLast5AtHome,
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* CENTER COLUMN */}
        {showCenter && (
          <div className="center-table">
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th
                    className="bg-gradient-to-r from-teal-800 to-indigo-900"
                    colSpan={5}
                  >
                    <HeaderWithIcon
                      icon={IoStatsChart}
                      text={t('teamsAverageGoals.averageGoals')}
                    />
                  </th>
                </tr>
                <tr>
                  <th
                    colSpan={2}
                    style={{ backgroundColor: 'rgba(45, 212, 191, 0.2)' }}
                  >
                    <HeaderWithIcon
                      icon={FaHome}
                      text={t('teamsAverageGoals.home')}
                    />
                  </th>
                  <th style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}></th>
                  <th
                    colSpan={2}
                    style={{ backgroundColor: 'rgba(79, 70, 229, 0.2)' }}
                  >
                    <HeaderWithIcon
                      icon={FaPlane}
                      text={t('teamsAverageGoals.away')}
                    />
                  </th>
                </tr>
                <tr>
                  <th>
                    <ColumnHeaderWithIcon
                      icon={BiFootball}
                      text={
                        columnToShow !== 'all'
                          ? t('teamsAverageGoals.scoredMin')
                          : t('teamsAverageGoals.scored')
                      }
                    />
                  </th>
                  <th>
                    <ColumnHeaderWithIcon
                      icon={FaShieldAlt}
                      text={
                        columnToShow !== 'all'
                          ? t('teamsAverageGoals.concededMin')
                          : t('teamsAverageGoals.conceded')
                      }
                    />
                  </th>
                  <th></th>
                  <th>
                    <ColumnHeaderWithIcon
                      icon={BiFootball}
                      text={
                        columnToShow !== 'all'
                          ? t('teamsAverageGoals.scoredMin')
                          : t('teamsAverageGoals.scored')
                      }
                    />
                  </th>
                  <th>
                    <ColumnHeaderWithIcon
                      icon={FaShieldAlt}
                      text={
                        columnToShow !== 'all'
                          ? t('teamsAverageGoals.concededMin')
                          : t('teamsAverageGoals.conceded')
                      }
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-teal-400 font-bold">
                    {selectedItemData.AverageGoals.HomeTeamAllScored}
                  </td>
                  <td className="text-red-400 font-bold">
                    {selectedItemData.AverageGoals.HomeTeamAllConceded}
                  </td>
                  <td className="text-xs text-center text-gray-300">
                    <IoStatsChart className="inline mr-1" />
                    {t('teamsAverageGoals.allMatches')}
                  </td>
                  <td className="text-teal-400 font-bold">
                    {selectedItemData.AverageGoals.AwayTeamAllScored}
                  </td>
                  <td className="text-red-400 font-bold">
                    {selectedItemData.AverageGoals.AwayTeamAllConceded}
                  </td>
                </tr>

                <tr>
                  <td className="text-teal-400 font-bold">
                    {selectedItemData.AverageGoals.HomeTeamOnlyHomeScored}
                  </td>
                  <td className="text-red-400 font-bold">
                    {selectedItemData.AverageGoals.HomeTeamOnlyHomeConceded}
                  </td>
                  <td className="text-xs text-center text-gray-300">
                    <FaExchangeAlt className="inline mr-1" />
                    {t('teamsAverageGoals.onlyHomeAway')}
                  </td>
                  <td className="text-teal-400 font-bold">
                    {selectedItemData.AverageGoals.AwayTeamOnlyAwayScored}
                  </td>
                  <td className="text-red-400 font-bold">
                    {selectedItemData.AverageGoals.AwayTeamOnlyAwayConceded}
                  </td>
                </tr>

                <tr>
                  <td className="text-teal-400 font-bold">
                    {selectedItemData.AverageGoals.HomeTeamLast5HomeScored}
                  </td>
                  <td className="text-red-400 font-bold">
                    {selectedItemData.AverageGoals.HomeTeamLast5HomeConceded}
                  </td>
                  <td className="text-xs text-center text-gray-300">
                    <FaHistory className="inline mr-1" />
                    {t('teamsAverageGoals.last5HomeAway')}
                  </td>
                  <td className="text-teal-400 font-bold">
                    {selectedItemData.AverageGoals.AwayTeamLast5AwayScored}
                  </td>
                  <td className="text-red-400 font-bold">
                    {selectedItemData.AverageGoals.AwayTeamLast5AwayConceded}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* RIGHT COLUMN */}
        {showRight && (
          <div className="right-table">
            <table style={{ fontSize: '12px' }}>
              <thead>
                <tr>
                  <th colSpan={4} style={getHeaderStyle(false)}>
                    <HeaderWithIcon
                      icon={FaClock}
                      text={t('teamsAverageGoals.averageGoalsHalfTime')}
                    />
                  </th>
                </tr>
                <tr>
                  <th
                    colSpan={3}
                    style={{ backgroundColor: 'rgba(79, 70, 229, 0.1)' }}
                  >
                    <HeaderWithIcon
                      icon={FaPlane}
                      text={t('teamsAverageGoals.awayTeam')}
                    />
                  </th>
                </tr>
                <tr>
                  <th>
                    <ColumnHeaderWithIcon
                      icon={BiFootball}
                      text={t('teamsAverageGoals.scored')}
                    />
                  </th>
                  <th></th>
                  <th>
                    <ColumnHeaderWithIcon
                      icon={FaShieldAlt}
                      text={t('teamsAverageGoals.conceded')}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-center text-teal-400 font-bold">
                    {formatNotAvailableValue(
                      selectedItemData.AverageGoals.HT_AwayScoredAway,
                    )}
                  </td>
                  <td className="text-center text-xs text-gray-400">
                    <FaPlane className="inline mr-1" />
                    {t('teamsAverageGoals.onlyAway')}
                  </td>
                  <td className="text-center text-red-400 font-bold">
                    {formatNotAvailableValue(
                      selectedItemData.AverageGoals.HT_AwayConcededAway,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="text-center text-teal-400 font-bold">
                    {formatNotAvailableValue(
                      selectedItemData.AverageGoals.HT_AwayScoredLast5Away,
                    )}
                  </td>
                  <td className="text-center text-xs text-gray-400">
                    <FaHistory className="inline mr-1" />
                    {t('teamsAverageGoals.last5Away')}
                  </td>
                  <td className="text-center text-red-400 font-bold">
                    {formatNotAvailableValue(
                      selectedItemData.AverageGoals.HT_AwayConcededLast5Away,
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
